import React, { useCallback, useReducer, useState } from "react";
import { Confirm, useDataProvider, useNotify, useRefresh, useShowController, useTranslate } from "react-admin";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useMediaQuery } from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { OfferIcon } from "../svg/OfferIcon";
import { ShowToolbar } from "./ShowToolbar";

const commonStyles = {
  toolbar: {
    textAlign: "right",
  },
  button: (theme: any) => ({
    marginLeft: theme.spacing(2),
  }),
  linkButton: (theme: any) => ({
    marginLeft: theme.spacing(8),
  }),
  accept: (theme: any) => ({
    color: "rgb(67, 160, 71)",
    marginRight: theme.spacing(2),
    "&:hover": {
      backgroundColor: "rgba(67, 160, 71, 0.12)",
    },
  }),
  reject: {
    color: "rgb(229, 57, 53)",
    "&:hover": {
      backgroundColor: "rgb(229, 57, 53, 0.12)",
    },
  },
  offerButton: {
    color: "rgb(30, 136, 229)",
    "&:hover": {
      backgroundColor: "rgb(30, 136, 229, 0.12)",
    },
  },
};

type State = boolean;
type Action = {
  type: string;
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case "fetching":
      return true;
    case "fetched":
      return false;
    default:
      throw new Error();
  }
}

const canAnswer = (permissions: any, data: any) => {
  if (!["admin", "staff"].includes(permissions)) {
    return false;
  }

  if (data.status !== "provisional") {
    return false;
  }

  return data.wholesaler_agreement_status === "waiting";
};

export const SpotContractDealShowActions = (props: { listPath: string; permissions: string }) => {
  const [isLoading, dispatch] = useReducer(reducer, false);

  const { listPath, permissions, ...actionProps } = props;
  const { record } = useShowController();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

  if (!permissions || !record) {
    return null;
  }
  const isPrenegotiatedDeal = record.offer_id === null;
  return (
    <ShowToolbar {...actionProps} listPath={listPath}>
      <Box sx={commonStyles.toolbar}>
        {!isMobile && canAnswer(permissions, record) && (
          <>
            <Box component="span" sx={commonStyles.button}>
              <AcceptButton {...actionProps} isLoading={isLoading} dispatch={dispatch} />
            </Box>
            <Box component="span" sx={commonStyles.button}>
              <RejectButton {...actionProps} isLoading={isLoading} dispatch={dispatch} />
            </Box>
          </>
        )}
        {!isMobile && !isPrenegotiatedDeal && (
          <Box component="span" sx={commonStyles.linkButton}>
            <OfferButton offerId={record.offer_id} />
          </Box>
        )}
      </Box>
    </ShowToolbar>
  );
};

const AcceptButton = (props: any) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const handleClickOpen = () => {
    setConfirmModalOpen(true);
  };
  const handleClickClose = () => {
    setConfirmModalOpen(false);
  };

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const { dispatch, isLoading } = props;
  const { record } = useShowController();
  const { id, version_number } = record;

  const handleClickAgreement = useCallback(async () => {
    dispatch({ type: "fetching" });
    const data = {
      wholesaler_agreement_status: "agreement",
      version_number,
    };
    const params = { id, data };
    try {
      const res = await dataProvider.update("spot_contract_deals", params);

      dispatch({ type: "fetched" });
      if (res.data.id === id) {
        notify("ra.notification.updated", { type: "info", messageArgs: { smart_count: 1 } });
        refresh();
      } else {
        notify("ra.message.error", { type: "error" });
      }
    } catch (e) {
      if (e instanceof Error) {
        notify(e.message, { type: "error" });
      }
    } finally {
      setConfirmModalOpen(false);
    }

    dispatch({ type: "fetched" });
  }, [dataProvider, dispatch, id, notify, refresh, version_number]);

  return (
    <>
      <Button
        sx={commonStyles.accept}
        size="small"
        startIcon={<CheckIcon />}
        disabled={isLoading}
        onClick={handleClickOpen}
      >
        {translate("ui.deals.agreement.agreement")}
      </Button>
      <Confirm
        isOpen={confirmModalOpen}
        title="取引案件の承諾"
        content="取引案件に承諾しますがよろしいですか？"
        onConfirm={handleClickAgreement}
        onClose={handleClickClose}
      />
    </>
  );
};

const RejectButton = (props: any) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const handleClickOpen = () => {
    setConfirmModalOpen(true);
  };
  const handleClickClose = () => {
    setConfirmModalOpen(false);
  };

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const { dispatch, isLoading } = props;
  const { record } = useShowController();
  const { id, version_number } = record;

  const handleClickDisagreement = useCallback(async () => {
    dispatch({ type: "fetching" });
    const data = {
      wholesaler_agreement_status: "disagreement",
      version_number,
    };
    const params = { id, data };

    try {
      const res = await dataProvider.update("spot_contract_deals", params);

      if (res.data.id === id) {
        notify("ra.notification.updated", { type: "info", messageArgs: { smart_count: 1 } });
        refresh();
      } else {
        notify("ra.message.error", { type: "error" });
      }
    } catch (e) {
      if (e instanceof Error) {
        notify(e.message, { type: "error" });
      }
    } finally {
      setConfirmModalOpen(false);
    }

    dispatch({ type: "fetched" });
  }, [dataProvider, dispatch, id, notify, refresh, version_number]);

  return (
    <>
      <Button
        sx={commonStyles.reject}
        size="small"
        startIcon={<CloseIcon />}
        disabled={isLoading}
        onClick={handleClickOpen}
      >
        {translate("ui.deals.agreement.disagreement")}
      </Button>
      <Confirm
        isOpen={confirmModalOpen}
        title="取引案件を不承諾"
        content="取引案件を不承諾にしますがよろしいですか？"
        onConfirm={handleClickDisagreement}
        onClose={handleClickClose}
      />
    </>
  );
};

const OfferButton = (props: { offerId: number }) => {
  const to = `/#/spot_contract_offers/${props.offerId}/show`;
  return (
    <Button
      sx={commonStyles.offerButton}
      size="small"
      startIcon={<OfferIcon width={18} height={18} color="#1e88e5" />}
      href={to}
    >
      オファー
    </Button>
  );
};
